import IMask from 'imask'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import Form from '../../../components/Form'
import FormItem from '../../../components/Form/FormItem'
import Button from '../../../components/General/Button'
import CurrencyInput from '../../../components/Inputs/CurrencyInput'
import InputText from '../../../components/Inputs/InputText'
import PercentInput from '../../../components/Inputs/PercentInput'
import Select from '../../../components/Inputs/Select'
import { TextArea } from '../../../components/Inputs/TextArea'
import { formatPipe } from '../../../utils/utils'

const formatPipeTotal = (
  value: string | number,
  unMaskValue: boolean = false,
  decimals: number = 2,
  min = 0
): any => {
  let unMask = unMaskValue
    ? [IMask.PIPE_TYPE.UNMASKED, IMask.PIPE_TYPE.TYPED]
    : []

  // Convertir número a string antes de procesar
  if (typeof value === 'number') {
    value = value.toString()
  }

  // Normalizar valores del backend: convertir punto decimal (US format) a coma (Latin format)
  // Solo si NO estamos unmasking y el valor NO tiene coma (indica valor raw del backend)
  if (!unMaskValue && typeof value === 'string' && !value.includes(',')) {
    value = value.replace('.', ',')
  }

  value = unMaskValue ? value.replace(/\./g, '') : value
  let numberPipe = IMask.createPipe(
    {
      mask: Number,
      thousandsSeparator: '.',
      radix: ',',
      scale: decimals,
      normalizeZeros: true,
      padFractionalZeros: decimals > 0,
      min,
    },
    ...unMask
  )
  // IMask().un
  return numberPipe(value + '')
}

const WithholdingForm: React.FC<IWithholdingForm> = ({
  onClose,
  onSubmit,
  title,
  fromPurchase = false,
  defaultValues = {},
}) => {
  // Función para formatear número a string con coma decimal (formato latino)
  const formatNumberForPipe = (value: number): string => {
    return Number(value).toFixed(2).replace('.', ',')
  }

  const form = useForm({
    defaultValues: {
      amountWithheld: defaultValues?.amountWithheld || 0,
      withholdingPercentage: defaultValues?.withholdingPercentage || 0,
      taxBase: defaultValues?.taxBase
        ? `Bs. ${formatPipe(formatNumberForPipe(defaultValues.taxBase))}`
        : 0,
      excempt: defaultValues?.excempt || 0,
      subtrahend: defaultValues?.subtrahend || 0,
      taxRate: defaultValues?.taxRate || { value: '16', text: '16%' },
      taxTotal: defaultValues?.taxTotal
        ? `Bs. ${formatPipe(formatNumberForPipe(defaultValues.taxTotal))}`
        : 0,
    },
  })

  let inputSubmitFileRef = useRef<HTMLInputElement | null>(null)
  const [document, setDocument] = useState<File | null>(null)
  const [selectedTaxRate, setSelectedTaxRate] = useState<{ value: string; text: string }>({ value: '16', text: '16%' })

  const onSubmitImage = (e): void => {
    e.preventDefault()
    if (inputSubmitFileRef.current && inputSubmitFileRef.current.files) {
      console.log('VER', inputSubmitFileRef?.current?.files[0])
      setDocument(inputSubmitFileRef?.current?.files[0])
    }
  }

  const submitForm = (values) => {
    let body = {}
    if (title === 'ISLR') {
      const amountWithheldValue = formatPipe(values.amountWithheld, true)
      body = {
        ...values,
        amountWithheld: amountWithheldValue,
        taxBase: formatPipe(values.taxBase, true),
        withholdingPercentage: formatPipe(values.withholdingPercentage, true),
        subtrahend: 0,
        taxTotal: amountWithheldValue,
        taxRate: 0,
        excempt: 0,
      }
    } else {
      const taxRateValue = typeof values.taxRate === 'object' ? values.taxRate.value : values.taxRate
      body = {
        ...values,
        amountWithheld: formatPipe(values.amountWithheld, true),
        taxBase: formatPipe(values.taxBase, true),
        taxTotal: formatPipeTotal(values.taxTotal, true),
        withholdingPercentage: formatPipe(values.withholdingPercentage, true),
        taxRate: taxRateValue,
        excempt: values.excempt ? formatPipe(values.excempt, true) : 0,
      }
    }
    onSubmit(body)
  }

  const percentInput = {
    ISLR: 'Alícuota Impuesto',
    'Impuesto Municipal': 'Alícuota Impuesto',
    OTHER: 'Alícuota Impuesto',
    IVA: 'Alícuota IVA',
  }

  const porcentajeRetenido = form.watch('withholdingPercentage')
  const taxBaseValue = form.watch('taxBase')

  // Función para parsear valor de moneda a número
  const parseCurrencyToNumber = (value: any): number => {
    if (typeof value === 'number') return value
    if (!value) return 0
    // Remover "Bs.", espacios, puntos de miles y convertir coma decimal a punto
    const cleaned = String(value)
      .replace(/Bs\.?\s*/gi, '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim()
    const num = parseFloat(cleaned)
    return isNaN(num) ? 0 : num
  }

  // Función para parsear porcentaje a número
  const parsePercentageToNumber = (value: any): number => {
    if (typeof value === 'number') return value
    if (!value) return 0
    // Remover "%" y convertir
    const cleaned = String(value).replace('%', '').replace(',', '.').trim()
    const num = parseFloat(cleaned)
    return isNaN(num) ? 0 : num
  }

  useEffect(() => {
    const percentage = parsePercentageToNumber(porcentajeRetenido)

    if (percentage > 0) {
      if (title === 'ISLR') {
        // Para ISLR: calcular monto retenido basado en la base imponible (subtotal)
        const baseImponible = parseCurrencyToNumber(taxBaseValue) || defaultValues?.taxBase || 0
        const value = baseImponible * (percentage / 100)
        form.setValue('amountWithheld', `Bs. ${formatPipe(value.toFixed(2))}`)
      } else {
        // Para IVA y otros: calcular basado en taxTotal
        // taxTotal = baseImponible * alícuota
        const baseImponible = parseCurrencyToNumber(taxBaseValue) || defaultValues?.taxBase || 0
        const alicuota = parseFloat(selectedTaxRate?.value || '16')
        const taxTotal = baseImponible * (alicuota / 100)

        // Actualizar el campo taxTotal
        form.setValue('taxTotal', `Bs. ${formatPipe(taxTotal.toFixed(2))}`)

        // Calcular monto retenido
        const value = taxTotal * (percentage / 100)
        form.setValue('amountWithheld', `Bs. ${formatPipe(value.toFixed(2))}`)
        form.setValue(
          'subtrahend',
          `Bs. ${formatPipe((taxTotal - value).toFixed(2))}`
        )
      }
    }
  }, [porcentajeRetenido, taxBaseValue, selectedTaxRate, title, defaultValues?.taxBase, form])

  return (
    <Form
      form={form}
      classes={`gap-x-6 gap-y-3`}
      onSubmit={(values) => submitForm({ ...values, document })}
    >
      <input type="file" ref={inputSubmitFileRef} onChange={onSubmitImage} />
      <h2 className="col-span-12 text-primary-900 font-bold">
        {title === 'OTHER' ? 'Otras Retenciones' : `Retención ${title || 'IVA'}`}
      </h2>

      <FormItem
        name="taxBase"
        rules={{ required: 'Es necesario indicar la base imponible' }}
        classes="xl:col-span-6 col-span-12"
      >
        {/* <InputNumber label="Base imponible" addDecimals={true} /> */}
        <CurrencyInput
          label="Base imponible"
          addDecimals={true}
          mask={`Bs. num`}
        />
      </FormItem>

      <FormItem
        name="withholdingPercentage"
        rules={{ required: 'Es necesario indicar el porcentaje retendio' }}
        classes="xl:col-span-6 col-span-12"
      >
        <PercentInput label="Porcentaje Retenido" />
      </FormItem>

      <FormItem
        name="amountWithheld"
        rules={{ required: 'Es necesario indicar el monto retenido' }}
        classes="xl:col-span-6 col-span-12"
      >
        {/* <InputNumber label="Monto retenido" addDecimals={true} /> */}
        <CurrencyInput
          label="Monto Retenido"
          addDecimals={true}
          mask={`Bs. num`}
        />
      </FormItem>

      {!fromPurchase && (
        <FormItem
          name="voucherNumber"
          rules={{ required: 'Es necesario indicar el nro de comprobante' }}
          classes="xl:col-span-6 col-span-12"
        >
          <InputText label="Nro Comprobante" />
        </FormItem>
      )}

      {title === 'IVA' && (
        <FormItem
          name="excempt"
          rules={{ required: 'Es necesario indicar el exento' }}
          classes="xl:col-span-6 col-span-12"
        >
          <CurrencyInput label="Exento" addDecimals={true} mask={`Bs. num`} />
        </FormItem>
      )}


      {title !== 'ISLR' && (
        <FormItem
          name={'taxRate'}
          rules={{ required: 'Es necesario indicar la alícuota IVA' }}
          classes="xl:col-span-6 col-span-12"
        >
          <Select
            label={percentInput[title]}
            options={[
              { value: '16', text: '16%' },
              { value: '8', text: '8%' },
            ]}
            value={selectedTaxRate}
            onChange={(val) => {
              setSelectedTaxRate(val)
              form.setValue('taxRate', val)
            }}
          />
        </FormItem>
      )}

      {/* <FormItem
        name={'taxRate'}
        rules={{ required: 'Es necesario indicar la alícuota IVA' }}
        classes="xl:col-span-6 col-span-12"
      >
        { title === 'ISLR' && (
          // <InputNumber label={percentInput[title]} addDecimals={true} />
          <CurrencyInput label={percentInput[title]} addDecimals={true} mask={`Bs. num`} />
        )}

        { title !== 'ISLR' && (
          <PercentInput
            label={percentInput[title]}
          />
        ) }
      </FormItem> */}

      {title !== 'ISLR' && (
        <FormItem
          name="taxTotal"
          rules={{ required: 'Es necesario indicar el total de impuesto' }}
          classes="xl:col-span-6 col-span-12"
        >
          <CurrencyInput
            label="Total impuesto"
            addDecimals={true}
            mask={`Bs. num`}
          />
        </FormItem>
      )}

      {title === 'OTHER' && (
        <FormItem
          name="description"
          rules={{ required: 'Es necesario indicar la descripción' }}
          classes="xl:col-span-6 col-span-12"
        >
          <TextArea label="Descripción" addDecimals={true} placeholder="" />
        </FormItem>
      )}

      {/* <div className="flex col-span-4 my-2 flex-column">
        <div className="flex flex-row">
          <IoMdAdd className="text-primary" />
          <u
            className="text-xs text-primary cursor-pointer"
            onClick={() =>
              inputSubmitFileRef.current && inputSubmitFileRef.current.click()
            }
            style={{ color: '#006AEA', cursor: 'pointer' }}
          >
            Subir documento
          </u>
        </div>
        <label
          style={{
            display: 'block',
            fontSize: 15,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'gray',
          }}
        >
          {document?.name}
        </label>
      </div> */}

      <div className="col-span-12 h-full flex flex-row mt-4">
        <div className="w-1/2 mr-5 pl-1">
          <Button
            onClick={() => {
              if (onClose) onClose()
            }}
            type="outline"
            classes="w-full"
          >
            Cancelar
          </Button>
        </div>
        <div className="w-1/2 pr-1">
          <Button type="primary" classes="w-full" htmlType="submit">
            Añadir
          </Button>
        </div>
      </div>
    </Form>
  )
}

interface IWithholdingForm {
  onClose: () => void
  onSubmit: (values: any) => void
  title: string
  fromPurchase?: boolean
  defaultValues?: any
}

export default WithholdingForm
